/**
 * Visualization Module for Gravity Assist
 * Handles canvas rendering for both planet frame and sun frame views
 */

import { GravityAssistPhysics, PhysicsState, Vector2D, TrajectoryPoint } from '../simulation/physics';

interface ColorScheme {
  background: string;
  grid: string;
  axes: string;
  planet: string;
  probe: string;
  trajectory: string;
  asymptote: string;
  impact: string;
  text: string;
  textDim: string;
}

export class Visualizer {
  private canvasPlanet: HTMLCanvasElement;
  private canvasSun: HTMLCanvasElement;
  private ctxPlanet: CanvasRenderingContext2D;
  private ctxSun: CanvasRenderingContext2D;
  private colors: ColorScheme;

  constructor(canvasPlanet: HTMLCanvasElement, canvasSun: HTMLCanvasElement) {
    this.canvasPlanet = canvasPlanet;
    this.canvasSun = canvasSun;

    const ctxPlanet = canvasPlanet.getContext('2d');
    const ctxSun = canvasSun.getContext('2d');

    if (!ctxPlanet || !ctxSun) {
      throw new Error('Failed to get canvas 2D context');
    }

    this.ctxPlanet = ctxPlanet;
    this.ctxSun = ctxSun;

    // Color scheme
    this.colors = {
      background: '#0a0e1a',
      grid: '#1a2332',
      axes: '#2a4a6a',
      planet: '#ff6b35',
      probe: '#00d9ff',
      trajectory: '#00ff88',
      asymptote: '#ffaa00',
      impact: '#ff0033',
      text: '#eee',
      textDim: '#888'
    };
  }

  /**
   * Panel A: Planet-centric frame
   * Shows trajectory in the reference frame of the planet
   */
  renderPlanetFrame(state: PhysicsState | null, animationTime: number | null = null): void {
    const ctx = this.ctxPlanet;
    const canvas = this.canvasPlanet;

    // Clear canvas
    ctx.fillStyle = this.colors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (!state || !state.trajectory) {
      this.drawNoData(ctx, canvas);
      return;
    }

    const { trajectory, rp, Rpl, u_in, b, zoom } = state;

    // Calculate scale - base view shows 1 million km by default
    const baseViewDistance = 1e6; // km
    const baseScale = Math.min(canvas.width, canvas.height) * 0.4 / baseViewDistance;
    const scale = baseScale * (zoom || 1);

    // Transform functions
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const toScreenX = (x: number) => centerX + x * scale;
    const toScreenY = (y: number) => centerY - y * scale; // Flip y-axis

    // Draw grid and axes
    this.drawGrid(ctx, canvas, centerX, centerY, scale);
    this.drawAxes(ctx, canvas, centerX, centerY);

    // Draw frame label
    ctx.fillStyle = this.colors.text;
    ctx.font = '18px sans-serif';
    ctx.fillText('Planet Frame', 15, 30);

    // Draw planet at origin
    this.drawPlanet(ctx, centerX, centerY, Rpl, scale);

    // Draw impact parameter line
    if (b !== undefined && u_in && state.U) {
      this.drawImpactParameter(ctx, b, scale, centerX, centerY, u_in, state.U);
    }

    // Draw periapsis circle
    ctx.strokeStyle = this.colors.textDim;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.arc(centerX, centerY, rp * scale, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw trajectory
    this.drawTrajectory(ctx, trajectory, toScreenX, toScreenY, animationTime);

    // Draw trajectory arrow
    this.drawTrajectoryArrow(ctx, trajectory, toScreenX, toScreenY);

    // Draw probe if animating
    if (animationTime !== null) {
      this.drawAnimatedProbe(ctx, trajectory, animationTime, toScreenX, toScreenY);
    } else {
      // Mark periapsis in static view
      this.drawPeriapsis(ctx, trajectory, toScreenX, toScreenY);
    }

    // Draw asymptote arrows showing incoming and outgoing directions
    const maxDist = Math.max(...trajectory.map(p =>
      Math.sqrt(p.x_P * p.x_P + p.y_P * p.y_P)
    ));
    this.drawAsymptotes(ctx, centerX, centerY, state, scale, maxDist);

    // Draw scattering angle annotation
    this.drawScatteringAngle(ctx, centerX, centerY, state, scale);

    // Scale indicator
    this.drawScaleIndicator(ctx, canvas, scale, 'km');
  }

  /**
   * Panel B: Sun frame
   * Shows trajectory in the inertial reference frame
   */
  renderSunFrame(state: PhysicsState | null, animationTime: number | null = null): void {
    const ctx = this.ctxSun;
    const canvas = this.canvasSun;

    // Clear canvas
    ctx.fillStyle = this.colors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (!state || !state.trajectory) {
      this.drawNoData(ctx, canvas);
      return;
    }

    const physics = new GravityAssistPhysics();
    const sunFrameTrajectory = physics.computeSunFrame(state.trajectory, state.v0);

    // Use same scale as planet frame
    const baseViewDistance = 1e6; // km
    const baseScale = Math.min(canvas.width, canvas.height) * 0.4 / baseViewDistance;
    const scale = baseScale * (state.zoom || 1);

    // Adjust center for animation
    let centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    if (animationTime !== null) {
      // Center follows planet during animation
      centerX = canvas.width / 2 - state.v0 * animationTime * scale;
    }

    const toScreenX = (x: number) => centerX + x * scale;
    const toScreenY = (y: number) => centerY - y * scale;

    // Draw grid and axes
    this.drawGrid(ctx, canvas, centerX, centerY, scale);
    this.drawAxes(ctx, canvas, centerX, centerY);

    // Draw frame label
    ctx.fillStyle = this.colors.text;
    ctx.font = '18px sans-serif';
    ctx.fillText('Sun Frame', 15, 30);

    // Draw results in upper right corner
    this.drawResults(ctx, state, canvas);

    // Draw planet trajectory (straight line)
    this.drawPlanetPath(ctx, sunFrameTrajectory, state.v0, toScreenX, toScreenY);

    // Draw planet at current position
    const planetX = animationTime !== null
      ? toScreenX(state.v0 * animationTime)
      : toScreenX(0);
    const planetY = toScreenY(0);

    this.drawPlanetBody(ctx, planetX, planetY, 0, animationTime !== null);

    // Draw probe trajectory
    this.drawSunFrameTrajectory(ctx, sunFrameTrajectory, toScreenX, toScreenY, animationTime);

    // Draw trajectory arrow
    this.drawSunFrameArrow(ctx, sunFrameTrajectory, toScreenX, toScreenY);

    // Draw probe if animating
    if (animationTime !== null) {
      this.drawAnimatedProbeInSunFrame(ctx, sunFrameTrajectory, animationTime, toScreenX, toScreenY);
    }

    // Scale indicator
    this.drawScaleIndicator(ctx, canvas, scale, 'km');
  }

  // ========== Drawing Helper Methods ==========

  private drawTrajectory(
    ctx: CanvasRenderingContext2D,
    trajectory: TrajectoryPoint[],
    toScreenX: (x: number) => number,
    toScreenY: (y: number) => number,
    animationTime: number | null
  ): void {
    if (animationTime !== null) {
      // Draw preview trajectory with low opacity
      ctx.strokeStyle = this.colors.trajectory + '33';
      ctx.lineWidth = 2;
      ctx.beginPath();
      trajectory.forEach((point, i) => {
        const sx = toScreenX(point.x_P);
        const sy = toScreenY(point.y_P);
        if (i === 0) ctx.moveTo(sx, sy);
        else ctx.lineTo(sx, sy);
      });
      ctx.stroke();

      // Draw solid trajectory up to current time
      ctx.strokeStyle = this.colors.trajectory;
      ctx.beginPath();
      let hasStarted = false;
      for (const point of trajectory) {
        if (point.t <= animationTime) {
          const sx = toScreenX(point.x_P);
          const sy = toScreenY(point.y_P);
          if (!hasStarted) {
            ctx.moveTo(sx, sy);
            hasStarted = true;
          } else {
            ctx.lineTo(sx, sy);
          }
        }
      }
      ctx.stroke();
    } else {
      // Static view: full trajectory
      ctx.strokeStyle = this.colors.trajectory;
      ctx.lineWidth = 2;
      ctx.beginPath();
      trajectory.forEach((point, i) => {
        const sx = toScreenX(point.x_P);
        const sy = toScreenY(point.y_P);
        if (i === 0) ctx.moveTo(sx, sy);
        else ctx.lineTo(sx, sy);
      });
      ctx.stroke();
    }
  }

  private drawTrajectoryArrow(
    ctx: CanvasRenderingContext2D,
    trajectory: TrajectoryPoint[],
    toScreenX: (x: number) => number,
    toScreenY: (y: number) => number
  ): void {
    const outgoingPoint = trajectory.reduce((max, p) => p.t > max.t ? p : max, trajectory[0]);
    const outgoingIndex = trajectory.findIndex(p => p === outgoingPoint);

    // Calculate arrow direction from trajectory tangent
    let arrowDirX: number, arrowDirY: number;
    if (outgoingIndex > 0 && outgoingIndex < trajectory.length - 1) {
      const prevPoint = trajectory[outgoingIndex - 1];
      const nextPoint = trajectory[outgoingIndex + 1];
      arrowDirX = nextPoint.x_P - prevPoint.x_P;
      arrowDirY = nextPoint.y_P - prevPoint.y_P;
    } else if (outgoingIndex === trajectory.length - 1) {
      const prevPoint = trajectory[outgoingIndex - 1];
      arrowDirX = outgoingPoint.x_P - prevPoint.x_P;
      arrowDirY = outgoingPoint.y_P - prevPoint.y_P;
    } else {
      const nextPoint = trajectory[outgoingIndex + 1];
      arrowDirX = nextPoint.x_P - outgoingPoint.x_P;
      arrowDirY = nextPoint.y_P - outgoingPoint.y_P;
    }

    // Normalize and draw arrow
    const arrowDirMag = Math.sqrt(arrowDirX * arrowDirX + arrowDirY * arrowDirY);
    const dirX = arrowDirX / arrowDirMag;
    const dirY = arrowDirY / arrowDirMag;

    const arrowSize = 12;
    const arrowX = toScreenX(outgoingPoint.x_P);
    const arrowY = toScreenY(outgoingPoint.y_P);

    ctx.fillStyle = this.colors.trajectory;
    ctx.beginPath();
    ctx.moveTo(arrowX, arrowY);
    ctx.lineTo(arrowX - arrowSize * dirX - arrowSize * 0.4 * dirY,
               arrowY + arrowSize * dirY - arrowSize * 0.4 * dirX);
    ctx.lineTo(arrowX - arrowSize * dirX + arrowSize * 0.4 * dirY,
               arrowY + arrowSize * dirY + arrowSize * 0.4 * dirX);
    ctx.closePath();
    ctx.fill();
  }

  private drawAnimatedProbe(
    ctx: CanvasRenderingContext2D,
    trajectory: TrajectoryPoint[],
    animationTime: number,
    toScreenX: (x: number) => number,
    toScreenY: (y: number) => number
  ): void {
    // Find closest point to current time
    let currentPoint = trajectory[0];
    let minDiff = Math.abs(trajectory[0].t - animationTime);
    for (const point of trajectory) {
      const diff = Math.abs(point.t - animationTime);
      if (diff < minDiff) {
        minDiff = diff;
        currentPoint = point;
      }
    }

    // Draw probe as small dot (same size as static view)
    ctx.fillStyle = this.colors.probe;
    ctx.beginPath();
    ctx.arc(toScreenX(currentPoint.x_P), toScreenY(currentPoint.y_P), 4, 0, 2 * Math.PI);
    ctx.fill();
  }

  private drawPeriapsis(
    ctx: CanvasRenderingContext2D,
    trajectory: TrajectoryPoint[],
    toScreenX: (x: number) => number,
    toScreenY: (y: number) => number
  ): void {
    const periapsisPoint = trajectory.find(p => Math.abs(p.t) < 1) ||
                          trajectory[Math.floor(trajectory.length / 2)];
    if (periapsisPoint) {
      ctx.fillStyle = this.colors.probe;
      ctx.beginPath();
      ctx.arc(toScreenX(periapsisPoint.x_P), toScreenY(periapsisPoint.y_P), 4, 0, 2 * Math.PI);
      ctx.fill();
    }
  }

  private drawPlanetPath(
    ctx: CanvasRenderingContext2D,
    sunFrameTrajectory: any[],
    v0: number,
    toScreenX: (x: number) => number,
    toScreenY: (y: number) => number
  ): void {
    ctx.strokeStyle = this.colors.planet + '80';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(toScreenX(sunFrameTrajectory[0].t * v0), toScreenY(0));
    ctx.lineTo(toScreenX(sunFrameTrajectory[sunFrameTrajectory.length - 1].t * v0), toScreenY(0));
    ctx.stroke();
    ctx.setLineDash([]);
  }

  private drawPlanetBody(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    _radius: number,
    _animated: boolean
  ): void {
    // Planet size is twice the probe size (probe is 4 pixels, so planet is 8)
    const planetRadius = 8;

    // Solid planet only, no glow even when animated
    ctx.fillStyle = this.colors.planet;
    ctx.beginPath();
    ctx.arc(x, y, planetRadius, 0, 2 * Math.PI);
    ctx.fill();
  }

  private drawSunFrameTrajectory(
    ctx: CanvasRenderingContext2D,
    trajectory: any[],
    toScreenX: (x: number) => number,
    toScreenY: (y: number) => number,
    animationTime: number | null
  ): void {
    if (animationTime !== null) {
      // Preview
      ctx.strokeStyle = this.colors.probe + '33';
      ctx.lineWidth = 2;
      ctx.beginPath();
      trajectory.forEach((point, i) => {
        const sx = toScreenX(point.x_S);
        const sy = toScreenY(point.y_S);
        if (i === 0) ctx.moveTo(sx, sy);
        else ctx.lineTo(sx, sy);
      });
      ctx.stroke();

      // Solid up to current time
      ctx.strokeStyle = this.colors.probe;
      ctx.beginPath();
      let hasStarted = false;
      for (const point of trajectory) {
        if (point.t <= animationTime) {
          const sx = toScreenX(point.x_S);
          const sy = toScreenY(point.y_S);
          if (!hasStarted) {
            ctx.moveTo(sx, sy);
            hasStarted = true;
          } else {
            ctx.lineTo(sx, sy);
          }
        }
      }
      ctx.stroke();
    } else {
      // Static full trajectory
      ctx.strokeStyle = this.colors.probe;
      ctx.lineWidth = 2;
      ctx.beginPath();
      trajectory.forEach((point, i) => {
        const sx = toScreenX(point.x_S);
        const sy = toScreenY(point.y_S);
        if (i === 0) ctx.moveTo(sx, sy);
        else ctx.lineTo(sx, sy);
      });
      ctx.stroke();
    }
  }

  private drawSunFrameArrow(
    ctx: CanvasRenderingContext2D,
    trajectory: any[],
    toScreenX: (x: number) => number,
    toScreenY: (y: number) => number
  ): void {
    const outgoingPoint = trajectory[trajectory.length - 1];
    const outgoingIndex = trajectory.length - 1;

    let arrowDirX: number, arrowDirY: number;
    if (outgoingIndex > 0) {
      const prevPoint = trajectory[outgoingIndex - 1];
      arrowDirX = outgoingPoint.x_S - prevPoint.x_S;
      arrowDirY = outgoingPoint.y_S - prevPoint.y_S;
    } else {
      const nextPoint = trajectory[1];
      arrowDirX = nextPoint.x_S - outgoingPoint.x_S;
      arrowDirY = nextPoint.y_S - outgoingPoint.y_S;
    }

    const arrowDirMag = Math.sqrt(arrowDirX * arrowDirX + arrowDirY * arrowDirY);
    const velDirX = arrowDirX / arrowDirMag;
    const velDirY = arrowDirY / arrowDirMag;

    const arrowSize = 12;
    const arrowX = toScreenX(outgoingPoint.x_S);
    const arrowY = toScreenY(outgoingPoint.y_S);

    ctx.fillStyle = this.colors.probe;
    ctx.beginPath();
    ctx.moveTo(arrowX, arrowY);
    ctx.lineTo(arrowX - arrowSize * velDirX - arrowSize * 0.4 * velDirY,
               arrowY + arrowSize * velDirY - arrowSize * 0.4 * velDirX);
    ctx.lineTo(arrowX - arrowSize * velDirX + arrowSize * 0.4 * velDirY,
               arrowY + arrowSize * velDirY + arrowSize * 0.4 * velDirX);
    ctx.closePath();
    ctx.fill();
  }

  private drawAnimatedProbeInSunFrame(
    ctx: CanvasRenderingContext2D,
    trajectory: any[],
    animationTime: number,
    toScreenX: (x: number) => number,
    toScreenY: (y: number) => number
  ): void {
    let currentPoint = trajectory[0];
    let minDiff = Math.abs(trajectory[0].t - animationTime);
    for (const point of trajectory) {
      const diff = Math.abs(point.t - animationTime);
      if (diff < minDiff) {
        minDiff = diff;
        currentPoint = point;
      }
    }

    // Draw probe as small dot (same size as static view)
    ctx.fillStyle = this.colors.probe;
    ctx.beginPath();
    ctx.arc(toScreenX(currentPoint.x_S), toScreenY(currentPoint.y_S), 4, 0, 2 * Math.PI);
    ctx.fill();
  }

  private drawGrid(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    centerX: number,
    centerY: number,
    scale: number
  ): void {
    ctx.strokeStyle = this.colors.grid;
    ctx.lineWidth = 1;

    const spacing = this.getGridSpacing(scale);
    const screenSpacing = spacing * scale;

    // Vertical lines
    for (let x = centerX % screenSpacing; x < canvas.width; x += screenSpacing) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }

    // Horizontal lines
    for (let y = centerY % screenSpacing; y < canvas.height; y += screenSpacing) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
  }

  private getGridSpacing(scale: number): number {
    const targetScreenSpacing = 50; // pixels
    const worldSpacing = targetScreenSpacing / scale;
    const magnitude = Math.pow(10, Math.floor(Math.log10(worldSpacing)));
    const normalized = worldSpacing / magnitude;
    let nice = 1;
    if (normalized > 5) nice = 10;
    else if (normalized > 2) nice = 5;
    else if (normalized > 1) nice = 2;
    return nice * magnitude;
  }

  private drawAxes(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    centerX: number,
    centerY: number
  ): void {
    ctx.strokeStyle = this.colors.axes;
    ctx.lineWidth = 2;

    // X-axis
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(canvas.width, centerY);
    ctx.stroke();

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, canvas.height);
    ctx.stroke();

    // Arrows
    const arrowSize = 10;
    ctx.beginPath();
    ctx.moveTo(canvas.width - arrowSize, centerY - arrowSize / 2);
    ctx.lineTo(canvas.width, centerY);
    ctx.lineTo(canvas.width - arrowSize, centerY + arrowSize / 2);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(centerX - arrowSize / 2, arrowSize);
    ctx.lineTo(centerX, 0);
    ctx.lineTo(centerX + arrowSize / 2, arrowSize);
    ctx.stroke();
  }

  private drawPlanet(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    _Rpl: number | undefined,
    _scale: number
  ): void {
    // Planet size is twice the probe size (probe is 4 pixels, so planet is 8)
    const radius = 8;

    // Solid planet only, no glow
    ctx.fillStyle = this.colors.planet;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fill();
  }

  private drawImpactParameter(
    ctx: CanvasRenderingContext2D,
    b: number,
    scale: number,
    centerX: number,
    centerY: number,
    u_in: Vector2D,
    U: number
  ): void {
    ctx.strokeStyle = this.colors.textDim;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    // Perpendicular direction to u_in
    const perpX = u_in.y / U;
    const perpY = -u_in.x / U;

    // Offset point
    const offsetX = b * perpX;
    const offsetY = b * perpY;

    // Direction along the line
    const dirX = u_in.x / U;
    const dirY = u_in.y / U;

    const farDist = 100000;
    const x1 = offsetX - farDist * dirX;
    const y1 = offsetY - farDist * dirY;
    const x2 = offsetX + farDist * dirX;
    const y2 = offsetY + farDist * dirY;

    ctx.beginPath();
    ctx.moveTo(centerX + x1 * scale, centerY - y1 * scale);
    ctx.lineTo(centerX + x2 * scale, centerY - y2 * scale);
    ctx.stroke();
    ctx.setLineDash([]);

    // Label
    ctx.fillStyle = this.colors.textDim;
    ctx.font = '12px monospace';
    const labelX = centerX + offsetX * scale + 10;
    const labelY = centerY - offsetY * scale - 5;
    ctx.fillText(`b = ${(b / 1000).toFixed(1)} × 10³ km`, labelX, labelY);
  }

  private drawAsymptotes(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    state: PhysicsState,
    scale: number,
    maxDist: number
  ): void {
    const { u_in, u_out, b } = state;
    const arrowLength = maxDist * scale * 0.7;

    ctx.strokeStyle = this.colors.asymptote;
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 5]);

    // Normalize direction vectors
    const u_in_mag = Math.sqrt(u_in.x * u_in.x + u_in.y * u_in.y);
    const u_in_dir_x = u_in.x / u_in_mag;
    const u_in_dir_y = u_in.y / u_in_mag;

    const u_out_mag = Math.sqrt(u_out.x * u_out.x + u_out.y * u_out.y);
    const u_out_dir_x = u_out.x / u_out_mag;
    const u_out_dir_y = u_out.y / u_out_mag;

    // Perpendicular vectors (choosing the side based on sign of b)
    const sign_b = b >= 0 ? 1 : -1;
    const perp_in_x = -u_in_dir_y * sign_b;
    const perp_in_y = u_in_dir_x * sign_b;
    const perp_out_x = -u_out_dir_y * sign_b;
    const perp_out_y = u_out_dir_x * sign_b;

    // Offset point on incoming asymptote (at distance |b| from planet)
    let offset_in_x = centerX + perp_in_x * Math.abs(b) * scale;
    let offset_in_y = centerY - perp_in_y * Math.abs(b) * scale;

    // Offset point on outgoing asymptote (at distance |b| from planet)
    let offset_out_x = centerX + perp_out_x * Math.abs(b) * scale;
    let offset_out_y = centerY - perp_out_y * Math.abs(b) * scale;

    // Flip to the opposite side of the planet
    offset_in_x = centerX - (offset_in_x - centerX);
    offset_in_y = centerY - (offset_in_y - centerY);
    offset_out_x = centerX - (offset_out_x - centerX);
    offset_out_y = centerY - (offset_out_y - centerY);

    // Calculate intersection point (same as in drawScatteringAngle)
    const p1_y_world = centerY - offset_in_y;
    const p2_y_world = centerY - offset_out_y;
    const det = u_in_dir_x * u_out_dir_y - u_in_dir_y * u_out_dir_x;

    let intersectX = offset_in_x;
    let intersectY = offset_in_y;

    if (Math.abs(det) > 1e-10) {
      const dx = offset_out_x - offset_in_x;
      const dy = p2_y_world - p1_y_world;
      const t1 = (dx * u_out_dir_y - dy * u_out_dir_x) / det;
      intersectX = offset_in_x + t1 * u_in_dir_x;
      const intersectY_world = p1_y_world + t1 * u_in_dir_y;
      intersectY = centerY - intersectY_world;
    }

    // Draw incoming asymptote
    const backwardExtend = arrowLength * 1.5; // How far back from planet
    const forwardExtend = arrowLength * 1.5;  // How far forward past planet

    // Main part (from far away to intersection) - full opacity
    ctx.strokeStyle = this.colors.asymptote;
    ctx.beginPath();
    ctx.moveTo(offset_in_x - u_in_dir_x * backwardExtend, offset_in_y + u_in_dir_y * backwardExtend);
    ctx.lineTo(intersectX, intersectY);
    ctx.stroke();

    // Extension past intersection (opposite side) - 30% opacity
    ctx.strokeStyle = this.colors.asymptote + '4D'; // Add 30% alpha (0.3 * 255 = 77 = 0x4D)
    ctx.beginPath();
    ctx.moveTo(intersectX, intersectY);
    ctx.lineTo(offset_in_x + u_in_dir_x * forwardExtend, offset_in_y - u_in_dir_y * forwardExtend);
    ctx.stroke();

    // Draw outgoing asymptote - only from intersection outward
    ctx.strokeStyle = this.colors.asymptote;
    ctx.beginPath();
    ctx.moveTo(intersectX, intersectY);
    ctx.lineTo(offset_out_x + u_out_dir_x * backwardExtend, offset_out_y - u_out_dir_y * backwardExtend);
    ctx.stroke();

    ctx.setLineDash([]);
  }

  private drawScatteringAngle(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    state: PhysicsState,
    scale: number
  ): void {
    const { u_in, u_out, b, delta } = state;

    // Normalize direction vectors (in world coordinates)
    const u_in_mag = Math.sqrt(u_in.x * u_in.x + u_in.y * u_in.y);
    const u_in_dir_x = u_in.x / u_in_mag;
    const u_in_dir_y = u_in.y / u_in_mag;

    const u_out_mag = Math.sqrt(u_out.x * u_out.x + u_out.y * u_out.y);
    const u_out_dir_x = u_out.x / u_out_mag;
    const u_out_dir_y = u_out.y / u_out_mag;

    // Work entirely in world coordinates (planet at origin)
    // Then convert to screen at the end

    // Perpendicular vectors (choosing the side based on sign of b)
    const sign_b = b >= 0 ? 1 : -1;
    const perp_in_x = -u_in_dir_y * sign_b;
    const perp_in_y = u_in_dir_x * sign_b;
    const perp_out_x = -u_out_dir_y * sign_b;
    const perp_out_y = u_out_dir_x * sign_b;

    // Points on the asymptotes at distance |b| from planet, then flipped to other side
    // In world coords (planet at origin)
    const p1_world_x = -perp_in_x * Math.abs(b);
    const p1_world_y = -perp_in_y * Math.abs(b);
    const p2_world_x = -perp_out_x * Math.abs(b);
    const p2_world_y = -perp_out_y * Math.abs(b);

    // Find intersection of the two asymptote lines in world coords
    // Line 1: p1 + t1 * u_in_dir
    // Line 2: p2 + t2 * u_out_dir
    // Solve: p1 + t1 * u_in = p2 + t2 * u_out

    const det = u_in_dir_x * u_out_dir_y - u_in_dir_y * u_out_dir_x;
    if (Math.abs(det) < 1e-10) return; // Lines are parallel

    const dx = p2_world_x - p1_world_x;
    const dy = p2_world_y - p1_world_y;

    const t1 = (dx * u_out_dir_y - dy * u_out_dir_x) / det;

    // Intersection point in world coords
    const intersect_world_x = p1_world_x + t1 * u_in_dir_x;
    const intersect_world_y = p1_world_y + t1 * u_in_dir_y;

    // Convert to screen coords
    const intersectX = centerX + intersect_world_x * scale;
    const intersectY = centerY - intersect_world_y * scale;

    // Screen angles for the asymptote directions (away from planet)
    // In screen coords, Y is flipped, so negate y component
    const angle_in_screen = Math.atan2(-u_in_dir_y, u_in_dir_x);
    const angle_out_screen = Math.atan2(-u_out_dir_y, u_out_dir_x);

    const radius = 80;

    ctx.strokeStyle = this.colors.asymptote;
    ctx.lineWidth = 1.5;
    ctx.beginPath();

    // Determine which direction to draw the arc
    // We want the arc that represents the scattering angle (the smaller arc between the asymptotes)
    // The arc should be on the exterior (convex) side, away from the planet

    // Normalize angles to [0, 2π)
    let a1 = angle_in_screen;
    let a2 = angle_out_screen;
    while (a1 < 0) a1 += 2 * Math.PI;
    while (a2 < 0) a2 += 2 * Math.PI;
    while (a1 >= 2 * Math.PI) a1 -= 2 * Math.PI;
    while (a2 >= 2 * Math.PI) a2 -= 2 * Math.PI;

    // Calculate clockwise and counterclockwise arc lengths
    let cwArc = a1 - a2;
    if (cwArc < 0) cwArc += 2 * Math.PI;
    let ccwArc = a2 - a1;
    if (ccwArc < 0) ccwArc += 2 * Math.PI;

    // Choose the arc that corresponds to the scattering angle delta
    // delta should be close to the smaller of the two arcs
    const useClockwise = Math.abs(cwArc - delta) < Math.abs(ccwArc - delta);

    if (useClockwise) {
      ctx.arc(intersectX, intersectY, radius, angle_in_screen, angle_out_screen, true);
    } else {
      ctx.arc(intersectX, intersectY, radius, angle_in_screen, angle_out_screen, false);
    }
    ctx.stroke();

    // Label positioning: place at the midpoint of the arc, slightly outside
    // Calculate the bisector angle (midpoint of the arc)
    let bisectorAngle: number;
    if (useClockwise) {
      // Clockwise from a1 to a2
      bisectorAngle = angle_in_screen - cwArc / 2;
    } else {
      // Counter-clockwise from a1 to a2
      bisectorAngle = angle_in_screen + ccwArc / 2;
    }

    // Place label outside the arc (at radius + offset)
    const labelDistance = radius + 20;
    const labelX = intersectX + Math.cos(bisectorAngle) * labelDistance;
    const labelY = intersectY + Math.sin(bisectorAngle) * labelDistance;

    ctx.fillStyle = this.colors.asymptote;
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`δ = ${(delta * 180 / Math.PI).toFixed(1)}°`, labelX, labelY);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  private drawScaleIndicator(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    scale: number,
    unit: string
  ): void {
    const targetLength = 100;
    const worldLength = targetLength / scale;

    // Round to nice number
    const magnitude = Math.pow(10, Math.floor(Math.log10(worldLength)));
    const normalized = worldLength / magnitude;
    let nice = 1;
    if (normalized > 5) nice = 10;
    else if (normalized > 2) nice = 5;
    else if (normalized > 1) nice = 2;

    const niceWorldLength = nice * magnitude;
    const niceScreenLength = niceWorldLength * scale;

    const x = canvas.width - 150;
    const y = canvas.height - 30;

    ctx.strokeStyle = this.colors.text;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + niceScreenLength, y);
    ctx.stroke();

    // Ticks
    ctx.beginPath();
    ctx.moveTo(x, y - 5);
    ctx.lineTo(x, y + 5);
    ctx.moveTo(x + niceScreenLength, y - 5);
    ctx.lineTo(x + niceScreenLength, y + 5);
    ctx.stroke();

    // Label - centered on the ruler
    ctx.fillStyle = this.colors.text;
    ctx.font = '12px monospace';
    const label = niceWorldLength >= 1000
      ? `${(niceWorldLength / 1000).toFixed(0)} × 10³ ${unit}`
      : `${niceWorldLength.toFixed(0)} ${unit}`;
    const labelWidth = ctx.measureText(label).width;
    const labelX = x + (niceScreenLength - labelWidth) / 2;
    ctx.fillText(label, labelX, y - 10);
  }

  private drawResults(ctx: CanvasRenderingContext2D, state: PhysicsState, canvas: HTMLCanvasElement): void {
    const lineHeight = 18;
    const y = 40;

    ctx.fillStyle = this.colors.text;
    ctx.font = '13px monospace';

    const formatWithSpaces = (num: number, decimals: number = 0): string => {
      const fixed = num.toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join('.');
    };

    const results = [
      `U: ${formatWithSpaces(state.U, 1)} km/s`,
      `δ: ${formatWithSpaces(state.delta * 180 / Math.PI, 1)}°`,
      `rₚ: ${formatWithSpaces(state.rp, 0)} km`,
      `|vᵢₙ|: ${formatWithSpaces(state.v_in_mag, 1)} km/s`,
      `|vₒᵤₜ|: ${formatWithSpaces(state.v_out_mag, 1)} km/s`,
      `ΔE: ${formatWithSpaces(state.deltaE, 0)} km²/s²`
    ];

    // Right-align the text
    ctx.textAlign = 'right';
    const x = canvas.width - 15;

    results.forEach((text, i) => {
      ctx.fillText(text, x, y + i * lineHeight);
    });

    // Reset text alignment
    ctx.textAlign = 'left';
  }

  private drawNoData(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): void {
    ctx.fillStyle = this.colors.textDim;
    ctx.font = '16px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('Adjust parameters to see visualization', canvas.width / 2, canvas.height / 2);
    ctx.textAlign = 'left';
  }
}
